<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { fetchContractTemplate, fetchCanonicalContractTemplate, saveContractTemplate } from '@/api/modules/contractTemplate'
import HtmlRichEditor from '@/components/HtmlRichEditor.vue'

const placeholderList = [
  '{ordernum}',
  '{name}',
  '{money}',
  '{months}',
  '{addtime}',
  '{hktime}',
  '{bankcard}',
  '{bankname}',
  '{mobile}',
  '{moneydx}',
  '{qm}',
]

const loading = ref(false)
const saving = ref(false)
const content = ref('')

async function reload() {
  loading.value = true
  try {
    const { data } = await fetchContractTemplate()
    content.value = data.content ?? ''
  } finally {
    loading.value = false
  }
}

async function loadCanonical() {
  try {
    const { data } = await fetchCanonicalContractTemplate()
    content.value = data.content ?? ''
    ElMessage.info('已载入系统默认模板（未保存；需点 Submit 写入数据库）')
  } catch {
    ElMessage.error('加载失败')
  }
}

async function submit() {
  saving.value = true
  try {
    const { data } = await saveContractTemplate(content.value)
    if (Number(data.status) === 1) {
      ElMessage.success(data.msg ?? '已保存')
      await reload()
    } else {
      ElMessage.warning(String(data.msg || '校验失败'))
    }
  } finally {
    saving.value = false
  }
}

onMounted(reload)
</script>

<template>
  <PageChrome
    title="合同模板"
    description="ThinkPHP Admin / System / ht：`ht.id=1` 合同 HTML。贷款列表「合同」、门户「Loan contract」均读取本模板并替换占位符；与后台预览、合同图一致。若库中为空，打开本页会自动带出系统默认正文。下方为富文本编辑器。"
  >
    <template #actions>
      <el-button :loading="loading" @click="reload">重新加载</el-button>
      <el-button @click="loadCanonical">载入系统默认</el-button>
      <el-button type="primary" :loading="saving" @click="submit">提交</el-button>
    </template>

    <el-alert type="info" show-icon :closable="false" style="margin-bottom: 18px">
      Substitution tokens:&nbsp;<code>{{ placeholderList.join(' · ') }}</code>
    </el-alert>

    <el-card shadow="hover">
      <template #header>
        <span class="tpl-head">合同内容</span>
      </template>
      <div
        class="tpl-editor-shell"
        v-loading="loading"
        element-loading-text="加载合同模板…"
      >
        <HtmlRichEditor
          v-model="content"
          editor-min-height="520px"
          :disabled="saving || loading"
        />
      </div>
    </el-card>
  </PageChrome>
</template>

<style scoped lang="scss">
.tpl-head {
  font-weight: 600;
}

.tpl-editor-shell {
  position: relative;
  min-height: 520px;
}
</style>
